import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export type ReviewSource = 'public_form' | 'admin_entry' | 'import' | string;

export interface AdminReview {
  id: string;
  customer_name: string;
  customer_location: string | null;
  content: string;
  rating: number;
  source: ReviewSource;
  is_approved: boolean;
  created_at: string;
}

export interface NewReviewInput {
  customer_name: string;
  customer_location: string;
  content: string;
  rating: number;
}

export const adminReviewsKey = (orgId: string | null | undefined) => ['admin-reviews', orgId] as const;

/** Every on-site review for the org (approved + pending), newest first. */
export function useAdminReviews(orgId: string | null | undefined) {
  return useQuery({
    queryKey: adminReviewsKey(orgId),
    enabled: Boolean(orgId),
    queryFn: async (): Promise<AdminReview[]> => {
      const { data, error } = await supabase
        .from('customer_reviews')
        .select('id, customer_name, customer_location, content, rating, source, is_approved, created_at')
        .eq('organization_id', orgId!)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as AdminReview[];
    },
  });
}

function useInvalidateReviews(orgId: string | null | undefined) {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: adminReviewsKey(orgId) });
    queryClient.invalidateQueries({ queryKey: ['customer-reviews', orgId] });
  };
}

/** Owner-entered testimonial — approved immediately. */
export function useAddReview(orgId: string | null | undefined) {
  const invalidate = useInvalidateReviews(orgId);
  return useMutation({
    mutationFn: async (input: NewReviewInput) => {
      if (!orgId) throw new Error('No organisation selected');
      const { error } = await supabase.from('customer_reviews').insert({
        organization_id: orgId,
        customer_name: input.customer_name.trim(),
        customer_location: input.customer_location.trim() || null,
        content: input.content.trim(),
        rating: input.rating,
        source: 'admin_entry',
        is_approved: true,
      });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });
}

/** Publish / unpublish via the SECURITY DEFINER RPC (stamps approved_by/at). */
export function useSetReviewApproval(orgId: string | null | undefined) {
  const invalidate = useInvalidateReviews(orgId);
  return useMutation({
    mutationFn: async ({ id, approved }: { id: string; approved: boolean }) => {
      const { error } = await supabase.rpc('set_customer_review_approval', { _review_id: id, _approved: approved });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });
}

export function useDeleteReview(orgId: string | null | undefined) {
  const invalidate = useInvalidateReviews(orgId);
  return useMutation({
    mutationFn: async (id: string) => {
      if (!orgId) throw new Error('No organisation selected');
      const { error } = await supabase.from('customer_reviews').delete().eq('id', id).eq('organization_id', orgId);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });
}
